import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api';

/**
 * 拉取数据的极简 hook。
 *
 * 控制台的每个页面都是「读一次、可能重读」的形态，因此只需要
 * loading / data / error / reload 四件事，不需要引入数据请求库。
 */
export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);

  // 组件卸载后不再 setState：切换页面时经常会有请求在路上。
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await loader();
      if (!alive.current) return;
      setData(result);
      setError(null);
    } catch (caught) {
      if (!alive.current) return;
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(500, { code: 'internal_error', message: '加载失败' }),
      );
    } finally {
      if (alive.current) setLoading(false);
    }
    // loader 每次渲染都是新函数，因此依赖由调用方显式给出。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, loading, reload: load, setData };
}
